import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy, Component, DestroyRef, inject, OnDestroy, OnInit, Renderer2, signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import {
  TnIconButtonComponent,
  TnTabComponent,
  TnTabPanelComponent,
  TnTabsComponent,
  TnTestIdDirective,
} from '@truenas/ui-components';
import { map } from 'rxjs/operators';
import { EnclosureMockService } from 'app/services/enclosure-mock.service';
import { AlertClassesTabComponent } from './components/alert-classes-tab/alert-classes-tab.component';
import { EnclosureMockTabComponent } from './components/enclosure-mock-tab/enclosure-mock-tab.component';
import { MockConfigurationsTabComponent } from './components/mock-configurations-tab/mock-configurations-tab.component';
import { WebSocketTabComponent } from './components/websocket-tab/websocket-tab.component';
import { storageKeys, tabs } from './constants';
import * as WebSocketDebugActions from './store/websocket-debug.actions';
import {
  selectIsPanelOpen, selectActiveTab, selectHasActiveMocks,
} from './store/websocket-debug.selectors';
import { safeGetItem } from './utils/local-storage-utils';

// Panel dimension constants
const panelWidthDefault = 550;
const panelWidthMin = 450;
const panelWidthMax = 900;

// Retry mechanism constants
const maxRetryAttempts = 30;
const retryIntervalMs = 100;

@Component({
  selector: 'ix-websocket-debug-panel',
  standalone: true,
  imports: [
    TnTabsComponent,
    TnTabComponent,
    TnTabPanelComponent,
    TnIconButtonComponent,
    TnTestIdDirective,
    WebSocketTabComponent,
    MockConfigurationsTabComponent,
    EnclosureMockTabComponent,
    AlertClassesTabComponent,
    TranslateModule,
  ],
  providers: [],
  host: { '(document:keydown)': 'handleKeyboardShortcut($event)' },
  templateUrl: './websocket-debug-panel.component.html',
  styleUrls: ['./websocket-debug-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebSocketDebugPanelComponent implements OnInit, OnDestroy {
  private store$ = inject(Store);
  private renderer = inject(Renderer2);
  private document = inject<Document>(DOCUMENT);
  // Ensure the service is instantiated
  enclosureMockService = inject(EnclosureMockService);
  private destroyRef = inject(DestroyRef);

  private readonly isPanelOpen$ = this.store$.select(selectIsPanelOpen);
  protected readonly isPanelOpen = toSignal(this.isPanelOpen$, { initialValue: false });
  private readonly activeTab$ = this.store$.select(selectActiveTab);
  readonly hasActiveMocks$ = this.store$.select(selectHasActiveMocks);
  private readonly tabIndexMap = [tabs.WEBSOCKET, tabs.MOCK_CONFIG, tabs.ENCLOSURE_MOCK, tabs.ALERT_CLASSES];

  protected readonly selectedTabIndex = toSignal(
    this.activeTab$.pipe(map((tab) => Math.max(0, this.tabIndexMap.indexOf(tab)))),
    { initialValue: 0 },
  );

  /** A signal: it changes from a `document` mousemove listener, which nothing else would re-render. */
  protected readonly panelWidth = signal(panelWidthDefault);
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    // Load saved mock configs and restore panel state
    this.store$.dispatch(WebSocketDebugActions.loadMockConfigs());
    this.store$.dispatch(WebSocketDebugActions.loadEnclosureMockConfig());

    // Ensure EnclosureMockService starts listening after configs are loaded
    // The service is injected in constructor, so it's already created

    // Restore panel state from localStorage asynchronously
    Promise.resolve().then(() => {
      const isOpen = safeGetItem<boolean>(storageKeys.PANEL_OPEN, false);
      if (isOpen) {
        this.store$.dispatch(WebSocketDebugActions.setPanelOpen({ isOpen }));
        // Immediately try to update margin when restoring panel state
        this.updateAdminLayoutMargin(true);
      }
    });

    // Manage body margin when panel opens/closes
    this.isPanelOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isOpen) => {
      this.updateAdminLayoutMargin(isOpen);
    });
  }

  private updateAdminLayoutMargin(isOpen: boolean, retryCount = 0): void {
    // Clear any existing retry timeout
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    const adminLayout = this.document.querySelector('.fn-maincontent') as HTMLElement;
    if (adminLayout) {
      try {
        if (isOpen) {
          this.renderer.setStyle(adminLayout, 'margin-right', `${this.panelWidth()}px`);
          this.renderer.setStyle(adminLayout, 'transition', 'margin-right 300ms cubic-bezier(0.4, 0, 0.2, 1)');
        } else {
          this.renderer.removeStyle(adminLayout, 'margin-right');
          this.renderer.removeStyle(adminLayout, 'transition');
        }
      } catch (error) {
        console.warn('Failed to update admin layout margin:', error);
      }
    } else if (isOpen && retryCount < maxRetryAttempts) {
      // If admin layout is not found yet and panel should be open, try again
      // Use exponential backoff for better performance
      const delay = Math.min(retryIntervalMs * (1.5 ** Math.floor(retryCount / 5)), 1000);
      this.retryTimeoutId = setTimeout(() => {
        this.updateAdminLayoutMargin(isOpen, retryCount + 1);
      }, delay);
    }
  }

  ngOnDestroy(): void {
    // Clean up retry timeout
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    // Clean up body margin on destroy
    const adminLayout = this.document.querySelector('.fn-maincontent') as HTMLElement;
    if (adminLayout) {
      try {
        this.renderer.removeStyle(adminLayout, 'margin-right');
        this.renderer.removeStyle(adminLayout, 'transition');
      } catch (error) {
        console.warn('Failed to clean up admin layout margin:', error);
      }
    }
  }

  onTabChange(index: number): void {
    const tab = this.tabIndexMap[index] ?? tabs.WEBSOCKET;
    this.store$.dispatch(WebSocketDebugActions.setActiveTab({ tab }));
  }

  togglePanel(): void {
    this.store$.dispatch(WebSocketDebugActions.togglePanel());
  }

  handleKeyboardShortcut(event: KeyboardEvent): void {
    if (event.ctrlKey && event.shiftKey && event.key === 'X') {
      event.preventDefault();
      this.togglePanel();
    }
  }

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = this.panelWidth();

    const handleMouseMove = (moveEvent: MouseEvent): void => {
      const diff = startX - moveEvent.clientX;
      this.panelWidth.set(Math.max(panelWidthMin, Math.min(panelWidthMax, startWidth + diff)));
      document.documentElement.style.setProperty('--debug-panel-width', `${this.panelWidth()}px`);

      // Update admin layout margin if panel is open
      if (this.isPanelOpen()) {
        this.updateAdminLayoutMargin(true);
      }
    };

    const handleMouseUp = (): void => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }
}
